import React, { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Activity,
  Server,
  Cpu,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Download,
  Play,
  Settings,
} from 'lucide-react';
import { useAdminPermissions } from '@/hooks/useAdminPermissions';
import { formatDistanceToNow } from 'date-fns';
import { logDebug } from '@/utils/errorHandler';

interface ServiceStatus {
  name: string;
  status: 'healthy' | 'warning' | 'critical' | 'offline';
  description: string;
  lastCheck: string;
  responseTime?: number;
  uptime?: string;
}

interface SystemResource {
  name: string;
  current: number;
  max: number;
  unit: string;
  status: 'healthy' | 'warning' | 'critical';
  history: number[];
}

interface HealthCheck {
  name: string;
  status: 'passed' | 'failed' | 'warning';
  message: string;
  details?: string;
  lastRun: string;
}

export function SystemHealth() {
  const { hasPermission } = useAdminPermissions();
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Mock data - replace with real API calls
  const [services] = useState<ServiceStatus[]>([
    {
      name: 'Web Server',
      status: 'healthy',
      description: 'Main application server',
      lastCheck: new Date(Date.now() - 1000 * 30).toISOString(),
      responseTime: 245,
      uptime: '15 days, 4 hours',
    },
    {
      name: 'Database',
      status: 'healthy',
      description: 'PostgreSQL primary database',
      lastCheck: new Date(Date.now() - 1000 * 45).toISOString(),
      responseTime: 12,
      uptime: '15 days, 4 hours',
    },
    {
      name: 'Redis Cache',
      status: 'healthy',
      description: 'In-memory data store',
      lastCheck: new Date(Date.now() - 1000 * 20).toISOString(),
      responseTime: 3,
      uptime: '15 days, 4 hours',
    },
    {
      name: 'Background Jobs',
      status: 'warning',
      description: 'Celery worker processes',
      lastCheck: new Date(Date.now() - 1000 * 60).toISOString(),
      responseTime: 1250,
      uptime: '14 days, 22 hours',
    },
    {
      name: 'File Storage',
      status: 'healthy',
      description: 'Media and static files',
      lastCheck: new Date(Date.now() - 1000 * 35).toISOString(),
      uptime: '15 days, 4 hours',
    },
    {
      name: 'Email Service',
      status: 'critical',
      description: 'SMTP email delivery',
      lastCheck: new Date(Date.now() - 1000 * 120).toISOString(),
      uptime: '12 days, 6 hours',
    },
  ]);

  const [resources] = useState<SystemResource[]>([
    {
      name: 'CPU Usage',
      current: 45,
      max: 100,
      unit: '%',
      status: 'healthy',
      history: [42, 38, 45, 52, 48, 45, 43, 47, 45, 42],
    },
    {
      name: 'Memory Usage',
      current: 6.8,
      max: 16,
      unit: 'GB',
      status: 'warning',
      history: [5.2, 5.8, 6.1, 6.5, 6.8, 6.9, 7.2, 6.8, 6.5, 6.8],
    },
    {
      name: 'Disk Usage',
      current: 245,
      max: 500,
      unit: 'GB',
      status: 'healthy',
      history: [240, 242, 243, 244, 245, 245, 246, 245, 244, 245],
    },
    {
      name: 'Network I/O',
      current: 125,
      max: 1000,
      unit: 'Mbps',
      status: 'healthy',
      history: [110, 115, 120, 125, 130, 125, 120, 125, 128, 125],
    },
  ]);

  const [healthChecks] = useState<HealthCheck[]>([
    {
      name: 'Database Connectivity',
      status: 'passed',
      message: 'All database connections active',
      lastRun: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
    },
    {
      name: 'API Response Time',
      status: 'passed',
      message: 'Average response time: 245ms',
      lastRun: new Date(Date.now() - 1000 * 60 * 3).toISOString(),
    },
    {
      name: 'Background Job Queue',
      status: 'warning',
      message: 'Queue processing slower than expected',
      details: '23 jobs in queue, average processing time: 45s',
      lastRun: new Date(Date.now() - 1000 * 60 * 2).toISOString(),
    },
    {
      name: 'SSL Certificate',
      status: 'passed',
      message: 'Certificate valid for 45 days',
      lastRun: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
    },
    {
      name: 'Backup Status',
      status: 'passed',
      message: 'Last backup completed successfully',
      details: 'Daily backup completed at 2:00 AM UTC',
      lastRun: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    },
    {
      name: 'Email Delivery',
      status: 'failed',
      message: 'Email service unreachable',
      details: 'SMTP server connection timeout',
      lastRun: new Date(Date.now() - 1000 * 60).toISOString(),
    },
  ]);

  const handleRefresh = async () => {
    setRefreshing(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setLastUpdate(new Date());
    setRefreshing(false);
  };

  const runHealthCheck = async (checkName: string) => {
    // Simulate running specific health check
    logDebug('Running health check', {
      component: 'SystemHealth',
      action: 'runHealthCheck',
      checkName,
    });
  };

  const downloadDiagnostics = () => {
    // Simulate downloading diagnostic report
    logDebug('Downloading diagnostic report', {
      component: 'SystemHealth',
      action: 'downloadDiagnostics',
    });
  };

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        setLastUpdate(new Date());
      }, 30000); // Refresh every 30 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'passed':
        return <CheckCircle className="size-4 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="size-4 text-yellow-600" />;
      case 'critical':
      case 'failed':
      case 'offline':
        return <XCircle className="size-4 text-red-600" />;
      default:
        return <Clock className="size-4 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'passed':
        return <Badge className="bg-green-100 text-green-800">Healthy</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-100 text-yellow-800">Warning</Badge>;
      case 'critical':
      case 'failed':
        return <Badge className="bg-red-100 text-red-800">Critical</Badge>;
      case 'offline':
        return <Badge className="bg-gray-100 text-gray-800">Offline</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Unknown</Badge>;
    }
  };

  const getResourceUsagePercent = (resource: SystemResource) => {
    return Math.round((resource.current / resource.max) * 100);
  };

  return (
    <AdminLayout title="System Health" description="Monitor system performance and service status">
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">
              Last updated: {formatDistanceToNow(lastUpdate, { addSuffix: true })}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="outline" size="sm" onClick={() => setAutoRefresh(!autoRefresh)}>
              <Activity className="mr-2 size-4" />
              Auto-refresh: {autoRefresh ? 'On' : 'Off'}
            </Button>
            <Button onClick={handleRefresh} variant="outline" disabled={refreshing} size="sm">
              <RefreshCw className={`mr-2 size-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            {hasPermission('admin.system.manage') && (
              <Button onClick={downloadDiagnostics} variant="outline" size="sm">
                <Download className="mr-2 size-4" />
                Download Report
              </Button>
            )}
          </div>
        </div>

        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="resources">Resources</TabsTrigger>
            <TabsTrigger value="checks">Health Checks</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* System Status Summary */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Overall Status</CardTitle>
                  <Server className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="size-6 text-green-600" />
                    <div className="text-2xl font-bold">Healthy</div>
                  </div>
                  <p className="text-xs text-muted-foreground">5 of 6 services operational</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">System Load</CardTitle>
                  <Cpu className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">45%</div>
                  <Progress value={45} className="mt-2 h-2" />
                  <p className="mt-1 text-xs text-muted-foreground">CPU utilization normal</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Uptime</CardTitle>
                  <Clock className="size-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">99.9%</div>
                  <p className="text-xs text-muted-foreground">15 days, 4 hours</p>
                </CardContent>
              </Card>
            </div>

            {/* Critical Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <AlertTriangle className="size-5 text-red-600" />
                  <span>Critical Issues</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Alert variant="destructive">
                  <XCircle className="size-4" />
                  <AlertTitle>Email Service Down</AlertTitle>
                  <AlertDescription>
                    SMTP server is unreachable. Email notifications are not being delivered.
                    <div className="mt-2">
                      <Button size="sm" variant="outline">
                        <Settings className="mr-2 size-4" />
                        Configure Email Settings
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="services" className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {services.map(service => (
                <Card key={service.name}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(service.status)}
                      <div>
                        <CardTitle className="text-base">{service.name}</CardTitle>
                        <CardDescription>{service.description}</CardDescription>
                      </div>
                    </div>
                    {getStatusBadge(service.status)}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      {service.responseTime && (
                        <div className="flex justify-between">
                          <span>Response Time:</span>
                          <span className="font-medium">{service.responseTime}ms</span>
                        </div>
                      )}
                      {service.uptime && (
                        <div className="flex justify-between">
                          <span>Uptime:</span>
                          <span className="font-medium">{service.uptime}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Last Check:</span>
                        <span className="font-medium">
                          {formatDistanceToNow(new Date(service.lastCheck), {
                            addSuffix: true,
                          })}
                        </span>
                      </div>
                    </div>
                    {hasPermission('admin.system.manage') && (
                      <div className="mt-4 flex space-x-2">
                        <Button size="sm" variant="outline">
                          <Play className="mr-2 size-4" />
                          Test
                        </Button>
                        <Button size="sm" variant="outline">
                          <Settings className="mr-2 size-4" />
                          Configure
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="resources" className="space-y-4">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {resources.map(resource => (
                <Card key={resource.name}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>{resource.name}</span>
                      {getStatusBadge(resource.status)}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <div className="mb-2 flex justify-between text-sm">
                          <span>Current Usage</span>
                          <span className="font-medium">
                            {resource.current} {resource.unit} / {resource.max} {resource.unit}
                          </span>
                        </div>
                        <Progress value={getResourceUsagePercent(resource)} className="h-3" />
                        <p className="mt-1 text-xs text-muted-foreground">
                          {getResourceUsagePercent(resource)}% utilized
                        </p>
                      </div>

                      {/* Simple history chart representation */}
                      <div>
                        <p className="mb-2 text-sm font-medium">Recent History</p>
                        <div className="flex h-16 items-end space-x-1">
                          {resource.history.map((value, index) => {
                            const height = (value / resource.max) * 100;
                            return (
                              <div
                                key={index}
                                className="flex-1 rounded-t-sm bg-blue-200"
                                style={{ height: `${Math.max(height, 2)}%` }}
                                title={`${value} ${resource.unit}`}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="checks" className="space-y-4">
            <div className="space-y-4">
              {healthChecks.map(check => (
                <Card key={check.name}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(check.status)}
                      <div>
                        <CardTitle className="text-base">{check.name}</CardTitle>
                        <CardDescription>{check.message}</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(check.status)}
                      {hasPermission('admin.system.manage') && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => runHealthCheck(check.name)}
                        >
                          <Play className="mr-2 size-4" />
                          Run Check
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  {check.details && (
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{check.details}</p>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Last run:{' '}
                        {formatDistanceToNow(new Date(check.lastRun), {
                          addSuffix: true,
                        })}
                      </p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
